import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ComponentCanDeactivate } from '@uigy/core/guards/component-can-deactivate';
import type { GetSensorsQuery } from '@uigy/generated/graphql';
import {
  Capability,
  EditSensorMutationVariables,
  LicenseStatus,
  SensorActiveStatus,
  SensorsBulkAction,
  SensorStatus,
  UpdatableStatus,
} from '@uigy/generated/graphql';
import { LicenseManagerService } from '@uigy/modules/license/license-manager.service';
import { EditSensorComponent } from '@uigy/modules/sensors/components/edit-sensor/edit-sensor.component';
import { SensorsGridSettingsService } from '@uigy/modules/sensors/constant/sensors-grid-settings.service';
import { EditSensorSliderData } from '@uigy/modules/sensors/interfaces/interfaces';
import { SensorsService } from '@uigy/modules/sensors/services/sensors.service';
import { DuplicatedNetworksService } from '@uigy/modules/settings/modules/assets/services/duplicated-networks.service';
import { SliderSizeEnumV2 } from '@uigy/shared/components/slider-v2/enums/slider-v2.enum';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { UiGridIdsEnum } from '@uigy/shared/components/ui-grid/enums/ui-grid-ids.enum';
import { CustomMenuItemParams } from '@uigy/shared/components/ui-grid/models/action-menu';
import { RowsSelection, UiGridFilterModel, UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { WizardPage } from '@uigy/shared/components/wizard/models/wizard-page';
import { WizardService } from '@uigy/shared/components/wizard/services/wizard.service';
import { TimeUnits } from '@uigy/shared/enums/timeUnits.enum';
import { ToggleData } from '@uigy/shared/models/interfaces';
import { ConfigService } from '@uigy/shared/services/config.service';
import { FlagsService } from '@uigy/shared/services/flags.service';
import { JobsV2Service } from '@uigy/shared/services/jobs-v2/jobs-v2.service';
import { JobComplete } from '@uigy/shared/services/jobs-v2/jobs-v2.utils';
import { LanguageService } from '@uigy/shared/services/language.service';
import { PopupService } from '@uigy/shared/services/popup.service';
import { combineLatest, Observable, Subject, tap } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

type SensorNode = GetSensorsQuery['sensors']['nodes'][number];
import { ActionButtonVariant } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { CopyTextRendererComponent } from '@uigy/shared/modules/grid/cell-renderers/copy-text-renderer/copy-text-renderer.component';

@Component({
    selector: 'app-sensors',
    templateUrl: './sensors.component.html',
    styleUrls: ['./sensors.component.scss'],
    standalone: false
})
export class SensorsComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
  public readonly sensorTexts = this.languageService.texts.settings.system_config.sensors;
  public readonly generalTexts = this.languageService.texts.general;

  public disableCheckForUpdates = signal<boolean>(false);
  public isSensorUpdateCapable = false;
  public toggleData = signal<ToggleData>({
    isOn: false,
    switchFunction: this.setSensorsAutoApproveConfig.bind(this),
    isDisabled: !this.isSensorUpdateCapable,
  });
  public isCheckingForUpdates = signal<boolean>(false);
  public canSensorsBeUpdated = false;

  private duplicatedNetworksService = inject(DuplicatedNetworksService);
  public gridOptions?: UiGridOptions<SensorNode, undefined>;
  public refetchData$ = new Subject<void>();

  constructor(private sensorsGridSettingsService: SensorsGridSettingsService,
              private sensorsService: SensorsService,
              private languageService: LanguageService,
              private popupService: PopupService,
              private toasterService: ToasterService,
              private wizardService: WizardService,
              private configService: ConfigService,
              private flagsService: FlagsService,
              private jobService: JobsV2Service,
              private licenseManagerService: LicenseManagerService,
              private cdr: ChangeDetectorRef,
  ) {
    super();
    this.setSensorsAutoApproveConfig = this.setSensorsAutoApproveConfig.bind(this);
  }

  public ngOnInit(): void {
    this.getSensorsAutoApproveConfig();
    this.isSensorUpdateCapable = this.sensorsService.isCapableToUpdateSensors();

    this.toggleData.update(data => ({
      ...data,
      isDisabled: !this.isSensorUpdateCapable,
    }));

    this.gridOptions = {
      gridId: UiGridIdsEnum.SENSORS,
      rowSelection: 'multiple',
      gridTitle: this.sensorTexts.title,
      dataSource: this.getAllSensors().pipe(tap(sensors => this.disableCheckForUpdates.set(sensors.length === 0))),
      columnDefs: this.sensorsGridSettingsService.ColumnDefs,
      actionMenu: this.sensorsActionMenu(),
      refetchData: this.refetchData$,
      exportFileName: this.sensorTexts.title,
      headerOptions: {
        globalButtons: [
          {
            variant: ActionButtonVariant.toggle,
            label: this.sensorTexts.toggle_text,
            toggleData: this.toggleData,
          },
         {
            variant: ActionButtonVariant.regular,
            button_variant: 'secondary',
            isDisabled: this.disableCheckForUpdates,
            action: this.checkForUpdates.bind(this),
            isLoadingState: this.isCheckingForUpdates,
            label: this.sensorTexts.update_sensor.check_for_updates,
          },
        ],
      },
      noRowsOverlayComponentParams: {
        title: this.sensorTexts.no_results.title,
        items: [
          {
            text: this.sensorTexts.no_results.description,
          },
          {
            text: this.sensorTexts.no_results.call_for_action,
          },
        ],
      },
      components: {
        copyTextRendererComponent: CopyTextRendererComponent,
      },
    };

  }

  public ngOnDestroy(): void {
    this.flagsService.renewFlagsSubscription();
  }

  public getAllSensors(): Observable<SensorNode[]> {
    return combineLatest([
      this.sensorsService.watchSensors(),
      this.licenseManagerService.getLicenseStatus().pipe(
        map((status) => status === LicenseStatus.Active)
      )
    ]).pipe(
      map(([sensors, isLicenseActive]) => {
        this.canSensorsBeUpdated = sensors.length > 0 && isLicenseActive;
        return sensors;
      }),
    );
  }

  public setSensorsAutoApproveConfig(value: boolean): void {
    this.configService.updateConfig({SensorsAutoApprove: value}).subscribe(
      () => this.getSensorsAutoApproveConfig()
    );
  }

  private sensorsActionMenu = (baseFilters?: UiGridFilterModel<SensorNode>) => {
    return (sensors: RowsSelection) => {
      {
        if(sensors.selectedRows.length > 1) {
          const selectedSensors = sensors.gridApi?.getSelectedRows() ?? [];
          return [this.useUpdateMenuItem(selectedSensors), ...this.bulkEditMenuItems(selectedSensors), ...this.bulkQueryConfigMenuItems(selectedSensors)];
        }

        const sensor = sensors.selectedRows.length === 1 ? sensors.gridApi?.getRowNode(sensors.selectedRows[0])?.data
            : sensors.gridApi?.getSelectedRows()[0] ?? undefined;

        if(!sensor) return [];

        const showUpdateActionButton = this.sensorsService.isCapableToUpdateSensors() && sensor.status !== SensorStatus.Updating;

        switch (sensor.status) {
          case SensorStatus.Connected:
            return [
            this.useEditMenuItem(sensor),
            this.usePauseMenuItem(sensor),
            this.useDeleteMenuItem(sensor),
            ...(showUpdateActionButton ? [this.useUpdateMenuItem([sensor])] : []),
          ];
          case SensorStatus.Paused:
            return [
              this.useEditMenuItem(sensor),
              this.useResumeMenuItem(sensor),
              this.useDeleteMenuItem(sensor),
              ...(showUpdateActionButton ? [this.useUpdateMenuItem([sensor])] : []),
            ];
          case SensorStatus.Pending:
            return [
              this.useApproveMenuItem(sensor),
              this.useDeleteMenuItem(sensor),
            ];
          case SensorStatus.Disconnected:
          case SensorStatus.NoTunnel:
            return [
              this.useDeleteMenuItem(sensor),
            ];
          case SensorStatus.ConnectedUnauthenticated:
            return [
              this.useEditMenuItem(sensor),
            ];
          case SensorStatus.Updating:
          default:
            return [];
        }
      }
    };
  };

  public editSensor(sensor: SensorNode): void {
    const editableSensor: EditSensorSliderData = {
      id: sensor.id,
      name: sensor.name,
      activeNetworks: sensor.activeNetworks,
      active: sensor.active === SensorActiveStatus.Enabled,
      status: sensor.status,
    };


    if (!sensor.networkArea?.id) {
      this.openEditWizard(sensor, editableSensor);
    } else {
      this.duplicatedNetworksService.getSensorsDuplicateNetwork(sensor.networkArea.id).pipe(
        map(res => {
          if (res.data.networkArea.networks && res.data.networkArea.networks.length > 0) {
            editableSensor.duplicateNetworks = res.data.networkArea.networks;
          }
          this.openEditWizard(sensor, editableSensor);
        })
      ).subscribe();
    }
  }

  private openEditWizard(sensor: SensorNode, editableSensor: EditSensorSliderData): void {
    this.wizardService.openWizardSlider({
      data: editableSensor,
      overlay: SliderSizeEnumV2.form,
      pages: [new WizardPage<EditSensorSliderData>(EditSensorComponent)],
      submitBtnText: this.generalTexts.buttons.save,
      header: this.sensorTexts.edit_sensor.title,
    }).subscribe((data) => {
        const editedSensor: EditSensorMutationVariables = {
          id: data.id,
          name: data.name,
          activeNetworks: sensor.status === SensorStatus.ConnectedUnauthenticated ? null : data.activeNetworks,
          active: sensor.status === SensorStatus.ConnectedUnauthenticated ? null : data.active,
        };
        this.sensorsService.editSensor(editedSensor).subscribe(() => {
          this.refetchData$.next();
        });
      }
    );
  }

  public deleteSensor(sensor: SensorNode): void {
    this.popupService.openPopup({
      header: this.sensorTexts.delete_sensor.title,
      text: this.sensorTexts.delete_sensor.text,
      submit: {
        title: this.generalTexts.buttons.delete,
        execute: (sensor: SensorNode) => this.sensorsService.deleteSensor(sensor).subscribe(() => {
          this.refetchData$.next();
        }),
        executeParams: sensor
      }
    });
  }

  public resumeSensor(sensor: SensorNode): void {
    this.sensorsService.resumeSensor(sensor).subscribe(() => {
      this.refetchData$.next();
    });
  }

  public approveSensor(sensor: SensorNode): void {
    this.sensorsService.approveSensor(sensor).subscribe(() => {
      this.refetchData$.next();
    });
  }

  public pauseSensor(sensor: SensorNode): void {
    this.popupService.openPopup({
      header: this.sensorTexts.pause_sensor.title,
      text: this.sensorTexts.pause_sensor.text,
      submit: {
        title: this.generalTexts.buttons.pause,
        execute: (sensor: SensorNode) => this.sensorsService.pauseSensor(sensor).subscribe(() => {
          this.refetchData$.next();
        }),
        executeParams: sensor
      }
    });
  }

  public update(sensors: SensorNode[]): void {
    const isBulk = sensors.length > 1;
    const isAllSensorsOldVersion = sensors.every(sensor => sensor.updatableSensor === UpdatableStatus.NonUpdatable);
    if(isAllSensorsOldVersion) {
      this.popupService.openPopup({
        header: this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.not_available_title', {count: sensors.length}),
        text: this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.not_available_dialog_text', {count: sensors.length}),
      });
      return;
    }

    this.popupService.openPopup({
      header: isBulk ? this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.title_other', {count: sensors.length}) : this.sensorTexts.update_sensor.title,
      text: isBulk ? this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.dialog_text_other', {count: sensors.length}) : this.sensorTexts.update_sensor.dialog_text,
      submit: {
        title: this.generalTexts.buttons.update,
        execute: (sensors: SensorNode[]) => this.sensorsService.updateSensors(sensors).pipe(
          take(1),
          tap(() => {
            // refresh the grid before the job is completed to update the sensors status
            this.refetchData$.next();
          }),
          this.jobService.handleJobOperator(),
          tap((job:JobComplete) => {
            this.onSuccessfulUpdate(job.value || '', sensors);
          })
        ).subscribe(() => {
          // refresh the grid after the job is completed
          this.refetchData$.next();
        }),
        executeParams: sensors
      },
    });
  }

  public changeBulkEditSensor(sensors: SensorNode[], action: SensorsBulkAction): void {
    this.sensorsService.bulkEditSensorQuery(sensors.map(sensor => sensor.id), action).pipe(
      take(1),
      tap(() => {
        this.refetchData$.next();
      }),
      this.jobService.handleJobOperator(),
      tap((job: JobComplete) => {
        this.onBulkEditDone(job.value || '', action);
      })
    ).subscribe(() => {
      this.refetchData$.next();
    });
  }

  public checkForUpdates(): void {
    this.isCheckingForUpdates.set(true);
    this.sensorsService.checkForUpdates().pipe(
      take(1),
      this.jobService.handleJobOperator(),
      tap((job:JobComplete) => {
        this.onCheckForUpdatesCompleted(job.value || '');
      }),
      finalize(() => {
        this.isCheckingForUpdates.set(false);
        this.cdr.markForCheck();
      }),
    ).subscribe();
  }

  /*  ACTION MENU ITEMS  */

  private useEditMenuItem(sensor: SensorNode): CustomMenuItemParams<SensorNode> {
    return {
      label: this.generalTexts.buttons.edit,
      onClick: () => this.editSensor(sensor),
      capability: Capability.WriteSensors
    };
  }

  private usePauseMenuItem(sensor: SensorNode): CustomMenuItemParams<SensorNode> {
    return {
      label: this.generalTexts.buttons.pause,
      onClick: () => this.pauseSensor(sensor),
      capability: Capability.WriteSensors
    };
  }

  private useResumeMenuItem(sensor: SensorNode): CustomMenuItemParams<SensorNode> {
    return {
      label: this.generalTexts.buttons.resume,
      onClick: () => this.resumeSensor(sensor),
      capability: Capability.WriteSensors
    };
  }

  private useDeleteMenuItem(sensor: SensorNode): CustomMenuItemParams<SensorNode> {
    const isDisabled = (sensor.networkArea?.networks ?? []).length > 0;
    return {
      label: this.generalTexts.buttons.delete,
      onClick: () => this.deleteSensor(sensor),
      capability: Capability.WriteSensors,
      disabled: isDisabled,
      tooltip: isDisabled ? this.sensorTexts.actions.delete.tooltip : ''
    };
  }

  private useApproveMenuItem(sensor: SensorNode): CustomMenuItemParams<SensorNode> {
    return {
      label: this.generalTexts.buttons.approve,
      onClick: () => this.approveSensor(sensor),
      capability: Capability.WriteSensors
    };
  }

  private useUpdateMenuItem(sensors: SensorNode[]): CustomMenuItemParams<SensorNode> {
    let isDisabled = !this.canSensorsBeUpdated || sensors.some(sensor => !this.sensorsService.canUpdate(sensor) || !this.sensorsService.isUpdateAvailable(sensor));
    return {
      label: this.generalTexts.buttons.update,
      onClick: () => this.update(sensors),
      capability: Capability.WriteSensors,
      disabled: isDisabled,
    };
  }

  private bulkQueryConfigMenuItems(sensors: SensorNode[]): CustomMenuItemParams<SensorNode>[] {
    let isDisabled = sensors.some(sensor => !this.sensorsService.canUpdate(sensor) || !(sensor.activeNetworks && sensor.activeNetworks.length > 0));
    return [{
      label: this.sensorTexts.actions.bulk_enable_active_queries,
      onClick: () => this.changeBulkEditSensor(sensors, SensorsBulkAction.EnableActive),
      disabled: isDisabled,
      capability: Capability.WriteSensors,
    },
      {
        label: this.sensorTexts.actions.bulk_disable_active_queries,
        onClick: () => this.changeBulkEditSensor(sensors, SensorsBulkAction.DisableActive),
        disabled: isDisabled,
        capability: Capability.WriteSensors,
      },
    ];
  }

  private bulkEditMenuItems(sensors: SensorNode[]): CustomMenuItemParams<SensorNode>[] {
    let commonStatus = [...new Set(sensors.map(sensor => sensor.status))];

    if (commonStatus.length === 1) {
      switch (commonStatus[0]) {
        case SensorStatus.Connected:
          return [
            {
              label: this.generalTexts.buttons.pause,
              onClick: () => this.changeBulkEditSensor(sensors, SensorsBulkAction.Pause),
              capability: Capability.WriteSensors,
            },
          ];
        case SensorStatus.Paused:
          return [
            {
              label: this.generalTexts.buttons.resume,
              onClick: () => this.changeBulkEditSensor(sensors, SensorsBulkAction.Resume),
              capability: Capability.WriteSensors,
            },
          ];
        default:
          return [];
      }
    }
    return [];
  }

  private onSuccessfulUpdate(response: string, sensors: SensorNode[]) {
    if(!response) return;
    const parsedResponse: { failed: string[], succeeded: string[]} = JSON.parse(response);
    const succeeded = parsedResponse.succeeded?.length || 0;
    const failed = parsedResponse.failed?.length || 0;
    const total = succeeded + failed;
    let sensorIp: string | undefined;
    if(total === 1) {
      sensorIp = sensors.find(sensor => sensor.id === parsedResponse.succeeded[0] || sensor.id === parsedResponse.failed[0])?.ip;
    }
    const successMessage = total > 1 ? this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.update_successful_other', {count: total, succeeded}) : this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.update_successful', {sensorIp});
    const errorMessage = total > 1 ? this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.update_failed_other', {count: total}) : this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.update_failed', {sensorIp});

    switch (true) {
      case succeeded === total: // If all the updates were successful
        this.toasterService.openToaster({
          message: successMessage,
          type: ToasterType.Success,
        });
        break;
      case succeeded > 0: // If some of the updates were successful
        this.toasterService.openToaster({
          message: successMessage,
          type: ToasterType.Warning,
        });
        break;
      default: // If no updates were successful
        this.toasterService.openToaster({
          message: errorMessage,
          type: ToasterType.Error,
        });
        break;
    }

    this.flagsService.renewFlagsSubscription(TimeUnits.fiveMinutes);
  }

  private onBulkEditDone(response: string, action: SensorsBulkAction) {
    if (!response) {
      return;
    }
    const parsedResponse: { operationSuccessCount: number, operationFailureCount: number } = JSON.parse(response);
    if (parsedResponse.operationFailureCount === 0) {
      const successMessage = this.sensorsService.bulkEditSensorsSuccessMessage(action, parsedResponse.operationSuccessCount);
      this.toasterService.openToaster({
        message: successMessage,
        type: ToasterType.Success,
      });
    }

  }

  private onCheckForUpdatesCompleted(response: string): void {
    if(!response) return;
    const parsedResponse: { anyUpdateExists: boolean } = JSON.parse(response);
    if(!parsedResponse) {
      return;
    }

    this.flagsService.renewFlagsSubscription(TimeUnits.fiveMinutes);
    this.toasterService.openToaster({
      type: ToasterType.Success,
      message: parsedResponse.anyUpdateExists ? this.sensorTexts.update_sensor.updates_available : this.sensorTexts.update_sensor.up_to_date,
    });
  }

  private getSensorsAutoApproveConfig(): void {
    this.configService.fetchConfig().subscribe(config => {
      this.toggleData.update(data => ({
        ...data,
        isOn: config.SensorsAutoApprove.Value,
      }));
      }
    );
  }
}
